import { useOnClickOutside } from 'components/utility/hooks/useOnClickOutside'
import React, { useCallback, useMemo, useRef, useState } from 'react'

const getDefaultIcon = (searchable) => {
  if (searchable) return <i className="fa-regular fa-magnifying-glass dome-color-med-grey"></i>
  return <i className="fa-light fa-solid fa-chevron-down dome-color-med-grey"></i>
}

export const Autocomplete = ({
  getTopOptionListItem,
  getBottomOptionListItem,
  getOptionLabel,
  getValueLabel,
  startIcon,
  loadingIcon = <i className="fa-solid fa-spinner fa-spin dome-color-dark-green"></i>,
  endIcon = <i className="fa-light fa-solid fa-chevron-down dome-color-med-grey"></i>,
  required,
  options = [],
  searchable,
  onInputChange,
  filterOptions,
  onSelect,
  labelKey,
  valueKey,
  isLoading,
  error,
  disableFilter,
  ...inputProps
}) => {
  const ref = useRef()
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [enableFiltering, setEnableFiltering] = useState(false)

  const handleClickInside = useCallback(() => setOpen(false), [])
  useOnClickOutside(ref, handleClickInside)

  const toggleOpen = useCallback(() => setOpen((prev) => !prev), [])

  const handleSelect = useCallback(
    (option, value) => {
      setOpen(false)
      onSelect(option)
      setEnableFiltering(false)
      setInputValue(value.toString())
    },
    [onSelect]
  )

  const value = getValueLabel ? getValueLabel() : inputValue

  const optionList = useMemo(
    () =>
      enableFiltering && !disableFilter
        ? options.filter((item) => {
            const itemLabel = getOptionLabel ? getOptionLabel(item) : item[labelKey]
            return filterOptions
              ? filterOptions(item)
              : itemLabel.toString().toLowerCase().includes(value.toLowerCase())
          })
        : options,
    [enableFiltering, options, filterOptions, value, disableFilter]
  )

  return (
    <div ref={ref} className="dome-d-flex dome-flex-column auto-questionnaire-auto-complete-field">
      <div onClick={toggleOpen}>
        <div className="auto-complete-field-search dome-cursor-pointer">
          {startIcon}
          <input
            value={value}
            required={required}
            readOnly={!searchable}
            onChange={(event) => {
              setOpen(true)
              setEnableFiltering(true)
              setInputValue(event.target.value)
              if (onInputChange) onInputChange(event)
            }}
            {...inputProps}
          />
          {isLoading && loadingIcon}
          {endIcon}
          {!endIcon && !startIcon && getDefaultIcon(searchable)}
        </div>
      </div>
      {open && (
        <ul className="auto-complete-field-list">
          {getTopOptionListItem && (
            <li
              onClick={(e) => setOpen(false)}
              className="auto-complete-field-list-item dome-cursor-pointer dome-p1 dome-color-darkest-grey dome-text-w300"
            >
              {getTopOptionListItem}
            </li>
          )}
          {optionList?.map((item) => {
            const itemLabel = getOptionLabel ? getOptionLabel(item) : item[labelKey]
            const itemKey = valueKey ? item[valueKey] : itemLabel
            return (
              <li
                key={itemKey}
                aria-selected={itemKey === value}
                onClick={() => handleSelect(item, itemLabel)}
                className="auto-complete-field-list-item dome-cursor-pointer dome-p1 dome-color-darkest-grey dome-text-w300"
              >
                {itemLabel}
              </li>
            )
          })}
          {getBottomOptionListItem && (
            <li
              onClick={(e) => setOpen(false)}
              className="auto-complete-field-list-item dome-cursor-pointer dome-p1 dome-color-darkest-grey dome-text-w300"
            >
              {getBottomOptionListItem}
            </li>
          )}
        </ul>
      )}
      {error && (
        <div style={{ color: '#F64141', fontSize: '12px', marginTop: '2px' }}>
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}
